/**
 * Organization product modules from login session `userData.Modules`.
 * Used to show permission cards and features only for modules the org owns.
 */

import { getLoginModuleRows } from "./loginModulesUtils";
import { isSponsorCroUserRecord } from "../constants/userTypeLabels";

function normalizeModuleName(name) {
  return String(name || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/** @returns {string[] | null} null when Modules is absent on session (legacy). */
export function getOrgModuleNames(userData) {
  const rows = getLoginModuleRows(userData);
  if (rows === null) return null;
  return rows.map((row) => normalizeModuleName(row.ModuleName)).filter(Boolean);
}

function orgHasModule(userData, matcher) {
  const names = getOrgModuleNames(userData);
  // Missing Modules on session must not grant every product — only show licensed modules.
  if (names === null) return false;
  if (names.length === 0) return false;
  return names.some(matcher);
}

/** Study & User Management bundle (Admin + Study permission cards). */
export function organizationHasStudyUserManagementModule(userData) {
  return orgHasModule(
    userData,
    (n) =>
      (n.includes("study") && n.includes("user")) ||
      /\bstudy\s*&\s*user\b/.test(n) ||
      /\bstudy\s+and\s+user\b/.test(n) ||
      n.includes("study management") ||
      /user\s*management/.test(n),
  );
}

/**
 * Org licensed for Study Management — used for study assignment on Add User.
 * Does not treat bare "User Management" alone as study access.
 */
export function organizationHasStudyManagementModule(userData) {
  return orgHasModule(
    userData,
    (n) =>
      n.includes("study management") ||
      (n.includes("study") && n.includes("user")) ||
      /\bstudy\s*&\s*user\b/.test(n) ||
      /\bstudy\s+and\s+user\b/.test(n),
  );
}

export function organizationHasIsfModule(userData) {
  return orgHasModule(
    userData,
    (n) =>
      /\beisf\b/.test(n) ||
      /\be\s*-?\s*isf\b/.test(n) ||
      (n.includes("isf") && !n.includes("planner") && !n.includes("psf")),
  );
}

/** Electronic Pharmacy Site File — separate licence from eISF. */
export function organizationHasEpsfModule(userData) {
  return orgHasModule(
    userData,
    (n) =>
      n === "epsf" ||
      n === "e-psf" ||
      n === "e psf" ||
      /\be\s*-?\s*psf\b/.test(n) ||
      /\bepsf\b/.test(n) ||
      n.includes("pharmacy site file"),
  );
}

export function organizationHasCapaModule(userData) {
  return orgHasModule(
    userData,
    (n) =>
      n === "capa" ||
      /\bcapa\b/.test(n) ||
      n.includes("corrective action") ||
      n.includes("preventive action"),
  );
}

export function organizationHasESourceModule(userData) {
  return orgHasModule(
    userData,
    (n) =>
      n === "e-source" ||
      n === "esource" ||
      n === "e source" ||
      /\be\s*-?\s*source\b/.test(n),
  );
}

function isTruthyOrgFlag(value) {
  return value === true || value === 1 || String(value ?? "").trim().toLowerCase() === "true";
}

/** Login / org-switch payload already includes the platform flag (true or false). */
export function loginSessionHasQmsTrainingAdditionalRolesFlag(userData) {
  if (!userData || typeof userData !== "object") return false;
  return (
    Object.prototype.hasOwnProperty.call(userData, "QmsTrainingAdditionalRolesEnabled") ||
    Object.prototype.hasOwnProperty.call(userData, "qmsTrainingAdditionalRolesEnabled")
  );
}

/** Login / org-switch payload already includes Sponsor/CRO management flag. */
export function loginSessionHasSponsorCroManagementFlag(userData) {
  if (!userData || typeof userData !== "object") return false;
  return (
    Object.prototype.hasOwnProperty.call(userData, "SponsorCroManagementEnabled") ||
    Object.prototype.hasOwnProperty.call(userData, "sponsorCroManagementEnabled")
  );
}

/** AQ Super Admin flag on customer org — not editable by customer Organisation Setup. */
export function organizationHasQmsTrainingAdditionalRolesEnabled(userData) {
  const raw =
    userData?.QmsTrainingAdditionalRolesEnabled ??
    userData?.qmsTrainingAdditionalRolesEnabled;
  return isTruthyOrgFlag(raw);
}

/** AQ Super Admin flag — Admin > Sponsor/CRO management for this customer org. */
export function organizationHasSponsorCroManagementEnabled(userData) {
  const raw =
    userData?.SponsorCroManagementEnabled ??
    userData?.sponsorCroManagementEnabled;
  return isTruthyOrgFlag(raw);
}

/**
 * Show QMS Training Additional Roles on user add/edit only when:
 * 1) org has QMS module licence, and
 * 2) AQ Super Admin enabled the feature for that org.
 */
export function organizationHasQmsTrainingAdditionalRolesFeature(userData) {
  return (
    organizationHasQmsModule(userData) &&
    organizationHasQmsTrainingAdditionalRolesEnabled(userData)
  );
}

/**
 * Show Admin > Sponsor/CRO management when:
 * 1) org has Study & User Management licence, and
 * 2) AQ Super Admin enabled Sponsor/CRO management for that org.
 */
export function organizationHasSponsorCroManagementFeature(userData) {
  return (
    organizationHasStudyUserManagementModule(userData) &&
    organizationHasSponsorCroManagementEnabled(userData)
  );
}

export function organizationHasQmsModule(userData) {
  return orgHasModule(
    userData,
    (n) =>
      n === "qms" ||
      n === "dms" ||
      /\bqms\b/.test(n) ||
      /\bdms\b/.test(n) ||
      (n.includes("document") && n.includes("management")) ||
      n.includes("quality management") ||
      n.includes("document control"),
  );
}

export function organizationHasEPlannerModule(userData) {
  return orgHasModule(
    userData,
    (n) =>
      n.includes("e-planner") ||
      n.includes("eplanner") ||
      /\be\s*planner\b/.test(n),
  );
}

export function organizationHasParticipantManagerModule(userData) {
  return orgHasModule(
    userData,
    (n) =>
      (n.includes("participant") && n.includes("manager")) ||
      n.includes("erecruitment") ||
      n.includes("e-recruitment") ||
      n.includes("e recruitment") ||
      (n.includes("recruitment") && !n.includes("planner")),
  );
}

export function organizationHasAnalyticsModule(userData) {
  return orgHasModule(
    userData,
    (n) => n === "analytics" || n.includes("analytics"),
  );
}

/** True when session lists at least one org product (legacy: Modules absent → show cards). */
export function organizationHasAnyLicensedProductModule(userData) {
  const names = getOrgModuleNames(userData);
  if (names === null) return true;
  return names.length > 0;
}

/**
 * Admin permission card (user/role/site management) — platform-level, not tied to Study only.
 */
export function organizationHasAdminPermissionCard(userData) {
  return organizationHasAnyLicensedProductModule(userData);
}

/**
 * Which permission cards to show on User Profile / Role Management.
 * Always scoped to org-licensed modules from `userData.Modules`.
 */
export function getOrgPermissionModuleVisibility(userData) {
  const studyUser = organizationHasStudyUserManagementModule(userData);
  const isf = organizationHasIsfModule(userData);
  const epsf = organizationHasEpsfModule(userData);
  const capa = organizationHasCapaModule(userData);
  const qms = organizationHasQmsModule(userData);
  const esource = organizationHasESourceModule(userData);

  return {
    admin: organizationHasAdminPermissionCard(userData),
    studyUser,
    isf,
    epsf,
    capa,
    qms,
    esource,
    ePlanner: organizationHasEPlannerModule(userData),
    participantManager: organizationHasParticipantManagerModule(userData),
    analytics: false,
  };
}

/**
 * Public FAQ / User Guide / AQ Assist module list visibility.
 *
 * Sponsor/CRO sessions: only My Inbox and eISF (and related binder names).
 *
 * Internal users:
 * - Always: Admin, My Inbox, Platform / Platform Navigation, Login & Access
 *   (and similar non-licence “general” topics)
 * - Plus: topics for modules the organisation is licensed for
 */
function isFaqGeneralAlwaysOnTopic(normalizedName) {
  const n = normalizedName;
  if (
    n.includes("login") ||
    n.includes("access") ||
    n.includes("account") ||
    n === "general" ||
    n === "platform" ||
    n.includes("platform navigation") ||
    n.includes("platform nav")
  ) {
    return true;
  }
  if (n === "admin" || n.startsWith("admin ") || n.includes("admin &")) {
    return true;
  }
  if (
    n.includes("my inbox") ||
    n === "inbox" ||
    (n.includes("inbox") && !n.includes("email"))
  ) {
    return true;
  }
  return false;
}

function isFaqInboxTopic(normalizedName) {
  const n = normalizedName;
  return (
    n.includes("my inbox") ||
    n === "inbox" ||
    (n.includes("inbox") && !n.includes("email"))
  );
}

function isFaqEisfTopic(normalizedName) {
  const n = normalizedName;
  return (
    /\beisf\b/.test(n) ||
    /\be\s*-?\s*isf\b/.test(n) ||
    (n.includes("isf") && !n.includes("planner") && !n.includes("psf"))
  );
}

function isFaqEpsfTopic(normalizedName) {
  const n = normalizedName;
  return (
    /\bepsf\b/.test(n) ||
    /\be\s*-?\s*psf\b/.test(n) ||
    n.includes("pharmacy site file") ||
    (n.includes("psf") && !n.includes("eisf"))
  );
}

function isSponsorCroFaqUser(userData) {
  return isSponsorCroUserRecord(userData?.IsCRO ?? userData?.IsCro);
}

export function organizationCanSeeFaqTopic(userData, topicName) {
  const n = normalizeModuleName(topicName);
  if (!n) return false;

  // Sponsor / CRO partners — only shared portal help topics.
  if (isSponsorCroFaqUser(userData)) {
    return (
      isFaqInboxTopic(n) ||
      isFaqEisfTopic(n) ||
      (isFaqEpsfTopic(n) && organizationHasEpsfModule(userData))
    );
  }

  // Internal staff — general topics always available.
  if (isFaqGeneralAlwaysOnTopic(n)) {
    return true;
  }

  if (
    n === "qms" ||
    n === "dms" ||
    /\bqms\b/.test(n) ||
    /\bdms\b/.test(n) ||
    n.includes("quality management") ||
    (n.includes("document") && n.includes("management"))
  ) {
    return organizationHasQmsModule(userData);
  }

  if (
    n === "capa" ||
    /\bcapa\b/.test(n) ||
    n.includes("corrective") ||
    n.includes("preventive")
  ) {
    return organizationHasCapaModule(userData);
  }

  if (isFaqEpsfTopic(n)) {
    return organizationHasEpsfModule(userData);
  }

  if (isFaqEisfTopic(n)) {
    return organizationHasIsfModule(userData);
  }

  if (
    n === "e-source" ||
    n === "esource" ||
    n.includes("e-source") ||
    n.includes("esource") ||
    n.includes("form management") ||
    n.includes("form builder")
  ) {
    return organizationHasESourceModule(userData);
  }

  if (
    n.includes("e-planner") ||
    n.includes("eplanner") ||
    /\be\s*planner\b/.test(n)
  ) {
    return organizationHasEPlannerModule(userData);
  }

  if (
    (n.includes("participant") && n.includes("manager")) ||
    n.includes("erecruitment") ||
    n.includes("e-recruitment") ||
    n.includes("e recruitment")
  ) {
    return organizationHasParticipantManagerModule(userData);
  }

  if (n === "ctms" || /\bctms\b/.test(n)) {
    return (
      organizationHasStudyManagementModule(userData) ||
      organizationHasStudyUserManagementModule(userData) ||
      organizationHasEPlannerModule(userData) ||
      organizationHasParticipantManagerModule(userData)
    );
  }

  if (n.includes("study")) {
    return (
      organizationHasStudyManagementModule(userData) ||
      organizationHasStudyUserManagementModule(userData)
    );
  }

  if (n.includes("analytics")) {
    return organizationHasAnalyticsModule(userData);
  }

  const names = getOrgModuleNames(userData);
  if (names === null || names.length === 0) return false;
  return names.some((orgName) => orgName.includes(n) || n.includes(orgName));
}

/**
 * Retired / not-yet-shipped product modules — hide from Contact Support
 * even if still present on the organisation licence row.
 */
function isHiddenFromContactSupport(name) {
  const n = normalizeModuleName(name);
  if (!n) return true;
  if (n === "analytics" || n.includes("analytics")) return true;
  if (
    n.includes("edelegation") ||
    n.includes("e-delegation") ||
    n.includes("e delegation") ||
    /\be[\s-]?delegation\b/.test(n)
  ) {
    return true;
  }
  return false;
}

/**
 * Contact Support module dropdown — same accessibility as FAQ topics.
 * Uses org-licensed session modules + FAQ always-on general topics,
 * filtered with `organizationCanSeeFaqTopic`, then appends Other.
 * Skips retired / unimplemented modules (Analytics, E-Delegation).
 */
export function getContactSupportModuleOptions(userData) {
  const seen = new Set();
  const list = [];

  const toSupportModuleLabel = (name) => {
    const n = normalizeModuleName(name);
    // Product name in UI is QMS; licence row may still say Document Management / DMS.
    if (
      n === "qms" ||
      n === "dms" ||
      n.includes("document management") ||
      n.includes("quality management") ||
      (n.includes("document") && n.includes("management"))
    ) {
      return "QMS";
    }
    return String(name || "").trim();
  };

  const add = (name) => {
    if (isHiddenFromContactSupport(name)) return;
    const trimmed = toSupportModuleLabel(name);
    const key = trimmed.toLowerCase();
    if (!trimmed || seen.has(key)) return;
    if (isHiddenFromContactSupport(trimmed)) return;
    // Keep licence check on the original name (Document Management still maps to QMS access).
    if (!organizationCanSeeFaqTopic(userData, name) && !organizationCanSeeFaqTopic(userData, trimmed)) {
      return;
    }
    seen.add(key);
    list.push({ value: trimmed, label: trimmed });
  };

  // Same non-licence topics FAQ always surfaces for eligible users.
  ["Admin", "My Inbox", "Platform", "Login & Access"].forEach(add);

  // Organisation-licensed product modules from login session.
  (getLoginModuleRows(userData) || []).forEach((row) => add(row.ModuleName));

  // Support catch-all (not a FAQ topic).
  list.push({ value: "Other", label: "Other" });
  return list;
}
